import { Injectable } from '@angular/core';
import { Beneficiary } from '@shared/common/interfaces/beneficiario.interface';
import {
    ESTADO_CIVIL_CASADO,
    ESTADO_CIVIL_CONVIVIENTE_CIVIL,
    PARENTESCOS_PERMITIDOS,
    CODIGO_PARENTESTO_HIJO_CON_INVALIDEZ,
    CODIGO_PARENTESTO_HIJO_O_HIJA,
    CODIGOS_PARENTESCO_CON_HIJOS,
    EDAD_MAXIMA_HIJO_BENEFICIARIO,
    TipoErrorValidacionBeneficiarios,
    CODIGO_ESTADO_CIVIL_SOLTERO,
    CODIGOS_CONYUGE,
    CODIGOS_CONVIVIENTE_CIVIL,
    CODIGO_PARENTESTO_CONYUGE_SIN_HIJOS_CON_DERECHO,
    CODIGO_PARENTESTO_CONVCIVIL_SIN_HIJOS_CON_DERECHO,
    CODIGO_PARENTESTO_CONYUGE_CON_HIJOS_CON_DERECHO,
    CODIGO_PARENTESTO_CONVCIVIL_CON_HIJOS_COMUN_CON_DERECHO
} from '@shared/common/constants/tramite-pension-constant';

/**
 * Resultado de validación de beneficiarios
 */
export interface ResultadoValidacionBeneficiarios {
    /** Errores mandatorios que detienen el flujo inmediatamente (solo se evalúa si existe alguno) */
    erroresMandatorios: TipoErrorValidacionBeneficiarios[];
    /** Errores agrupados que requieren evaluación ordenada */
    erroresAgrupados: TipoErrorValidacionBeneficiarios[];
}

/**
 * Service para validar beneficiarios según reglas de negocio del trámite de pensión
 */
@Injectable({
    providedIn: 'root',
})
export class BeneficiariosValidadorService {
    /**
     * Centraliza todas las validaciones de beneficiarios
     * Acumula todos los errores encontrados para mostrarlos al usuario de una vez
     * @param beneficiaries Array de beneficiarios obtenidos del servicio
     * @param estadoCivil Estado civil del afiliado
     * @returns Objeto con el resultado de la validación y array de errores encontrados
     */
    validarBeneficiarios(
        beneficiaries: Beneficiary[] | null,
        estadoCivil: string,
        apellidoPaterno: string
    ): ResultadoValidacionBeneficiarios {
        const erroresAgrupadosSet = new Set<TipoErrorValidacionBeneficiarios>();

        // 1. Validar errores mandatorios primero (sin procesar toda la lógica)
        const errorMandatorio = this.validarErroresMandatorios(beneficiaries);
        if (errorMandatorio) {
            return {
                erroresMandatorios: [errorMandatorio],
                erroresAgrupados: []
            };
        }

        // 2. Verificar si requiere beneficiarios según estado civil
        const requiereBeneficiarios = this.requiereBeneficiariosPorEstadoCivil(estadoCivil);

        // 3. Procesar todos los beneficiarios y validaciones de conjunto
        this.procesarBeneficiarios(
            beneficiaries!,
            estadoCivil,
            requiereBeneficiarios,
            erroresAgrupadosSet,
            apellidoPaterno
        );

        const erroresAgrupados = Array.from(erroresAgrupadosSet);

        return {
            erroresMandatorios: [],
            erroresAgrupados
        };
    }

    /**
     * Valida errores mandatorios antes de procesar toda la lógica
     * Hace un recorrido rápido solo para detectar errores críticos
     * @param beneficiaries Array de beneficiarios a validar (puede ser null)
     * @returns Tipo de error mandatorio encontrado o null si no hay errores
     */
    private validarErroresMandatorios(
        beneficiaries: Beneficiary[] | null
    ): TipoErrorValidacionBeneficiarios | null {
        // Validar si hubo error al obtener beneficiarios
        if (beneficiaries === null) {
            return TipoErrorValidacionBeneficiarios.ERROR_SERVICIO;
        }

        // Validar hijo con invalidez (error mandatorio)
        if (beneficiaries.some(b => b.codigoParentesco === CODIGO_PARENTESTO_HIJO_CON_INVALIDEZ)) {
            return TipoErrorValidacionBeneficiarios.BENEFICIARIO_CON_INVALIDEZ;
        }

        return null;
    }

    /**
     * Procesa todos los beneficiarios acumulando información y errores
     * Incluye validaciones de elementos individuales y validaciones de conjunto
     * @param beneficiaries Array de beneficiarios a procesar
     * @param estadoCivil Estado civil del afiliado
     * @param requiereBeneficiarios Si requiere beneficiario por estado civil
     * @param erroresAgrupados Set de errores agrupados
     * @param apellidoPaterno Apellido paterno del afiliado
     */
    private procesarBeneficiarios(
        beneficiaries: Beneficiary[],
        estadoCivil: string,
        requiereBeneficiarios: boolean,
        erroresAgrupados: Set<TipoErrorValidacionBeneficiarios>,
        apellidoPaterno: string
    ): void {
        const estado = {
            tieneBeneficiarioConHijos: false,
            tieneHijoEnLista: false,
            tieneBeneficiarioRequerido: false,
        };
        const estadoHijosPareja = {
            existeHijoComunConPareja: false,
            existeHijoSoloCausante: false,
        };
        const contextoPareja = this.obtenerContextoParejaParaHijos(beneficiaries);
        const apellidoPaternoNorm = this.normalizarApellido(apellidoPaterno);

        for (const beneficiario of beneficiaries) {
            this.actualizarEstadoBaseBeneficiario(
                beneficiario,
                estadoCivil,
                requiereBeneficiarios,
                estado,
                erroresAgrupados
            );

            if (beneficiario.codigoParentesco !== CODIGO_PARENTESTO_HIJO_O_HIJA) {
                continue;
            }

            if (this.validarHijo(beneficiario) === null) {
                estado.tieneHijoEnLista = true;
            }
            this.procesarValidacionesDeHijo(
                beneficiario,
                apellidoPaterno,
                apellidoPaternoNorm,
                contextoPareja,
                estadoHijosPareja,
                erroresAgrupados
            );
        }

        this.validarReglaHijosSoloCausanteConPareja(
            contextoPareja,
            estadoHijosPareja,
            erroresAgrupados
        );

        // Validaciones de conjunto (requieren haber procesado toda la lista)
        this.validarConjuntoBeneficiarios(
            requiereBeneficiarios,
            estado.tieneBeneficiarioRequerido,
            estado.tieneBeneficiarioConHijos,
            estado.tieneHijoEnLista,
            erroresAgrupados
        );
    }

    private obtenerContextoParejaParaHijos(beneficiaries: Beneficiary[]): {
        tieneConyugeoConvivienteCivilSinHijos: boolean;
        tieneConyugeoConvivienteCivilConHijos: boolean;
        apellidoPaternoPareja: string;
    } {
        const hayHijoEnLista = beneficiaries.some(
            b => b.codigoParentesco === CODIGO_PARENTESTO_HIJO_O_HIJA
        );        
        if (!hayHijoEnLista) {
            return {
                tieneConyugeoConvivienteCivilSinHijos: false,
                tieneConyugeoConvivienteCivilConHijos: false,
                apellidoPaternoPareja: '',
            };
        }

        const tieneConyugeoConvivienteCivilSinHijos = beneficiaries.find(
            b => b.codigoParentesco === CODIGO_PARENTESTO_CONYUGE_SIN_HIJOS_CON_DERECHO ||
                b.codigoParentesco === CODIGO_PARENTESTO_CONVCIVIL_SIN_HIJOS_CON_DERECHO
        );

        const parejaConHijos = beneficiaries.find(
            b => b.codigoParentesco === CODIGO_PARENTESTO_CONYUGE_CON_HIJOS_CON_DERECHO ||
                b.codigoParentesco === CODIGO_PARENTESTO_CONVCIVIL_CON_HIJOS_COMUN_CON_DERECHO
        );

        return {
            tieneConyugeoConvivienteCivilSinHijos: !!tieneConyugeoConvivienteCivilSinHijos,
            tieneConyugeoConvivienteCivilConHijos: !!parejaConHijos,
            apellidoPaternoPareja: parejaConHijos?.apellidoPaterno || tieneConyugeoConvivienteCivilSinHijos?.apellidoPaterno || '',
        };
    }

    private actualizarEstadoBaseBeneficiario(
        beneficiario: Beneficiary,
        estadoCivil: string,
        requiereBeneficiarios: boolean,
        estado: {
            tieneBeneficiarioConHijos: boolean;
            tieneHijoEnLista: boolean;
            tieneBeneficiarioRequerido: boolean;
        },
        erroresAgrupados: Set<TipoErrorValidacionBeneficiarios>
    ): void {
        const { codigoParentesco } = beneficiario;

        if (requiereBeneficiarios && !estado.tieneBeneficiarioRequerido) {
            estado.tieneBeneficiarioRequerido = this.esParentescoRequeridoPorEstadoCivil(
                codigoParentesco,
                estadoCivil
            );
        }

        if (!PARENTESCOS_PERMITIDOS.includes(codigoParentesco)) {
            erroresAgrupados.add(TipoErrorValidacionBeneficiarios.PARENTESCO_NO_PERMITIDO);
        }

        if (CODIGOS_PARENTESCO_CON_HIJOS.includes(codigoParentesco)) {
            estado.tieneBeneficiarioConHijos = true;
        }
    }

    private procesarValidacionesDeHijo(
        beneficiario: Beneficiary,
        apellidoPaterno: string,
        apellidoPaternoNorm: string,
        contextoPareja: {
            tieneConyugeoConvivienteCivilSinHijos: boolean;
            tieneConyugeoConvivienteCivilConHijos: boolean;
            apellidoPaternoPareja: string;
        },
        estadoHijosPareja: {
            existeHijoComunConPareja: boolean;
            existeHijoSoloCausante: boolean;
        },
        erroresAgrupados: Set<TipoErrorValidacionBeneficiarios>
    ): void {
        const validacionHijo = this.validarHijo(beneficiario);
        if (validacionHijo !== null) {
            erroresAgrupados.add(TipoErrorValidacionBeneficiarios.HIJO_NO_MENOR_24_SOLTERO);
            return;
        }
        
        if (contextoPareja.tieneConyugeoConvivienteCivilSinHijos && this.validarApellidosHijosEnComun(
            beneficiario,
            apellidoPaterno,
            contextoPareja.apellidoPaternoPareja
        )) {
            erroresAgrupados.add(TipoErrorValidacionBeneficiarios.CONYUGE_SIN_HIJOS_TIENE_HIJO_COMUN);
            return;
        }
        if (!this.hijoTieneApellidoCausanteEnCualquierPosicion(beneficiario, apellidoPaternoNorm)) {
            erroresAgrupados.add(TipoErrorValidacionBeneficiarios.HIJO_SIN_APELLIDO_CAUSANTE);
            return;
        }

        if (!contextoPareja.tieneConyugeoConvivienteCivilConHijos) {
            return;
        }

        const esHijoComun = this.validarApellidosHijosEnComun(
            beneficiario,
            apellidoPaterno,
            contextoPareja.apellidoPaternoPareja
        );

        if (esHijoComun) {
            estadoHijosPareja.existeHijoComunConPareja = true;
            return;
        }

        estadoHijosPareja.existeHijoSoloCausante = true;
    }

    private validarReglaHijosSoloCausanteConPareja(
        contextoPareja: {
            tieneConyugeoConvivienteCivilSinHijos: boolean;
            tieneConyugeoConvivienteCivilConHijos: boolean;
            apellidoPaternoPareja: string;
        },
        estadoHijosPareja: {
            existeHijoComunConPareja: boolean;
            existeHijoSoloCausante: boolean;
        },
        erroresAgrupados: Set<TipoErrorValidacionBeneficiarios>
    ): void {
        if (!contextoPareja.tieneConyugeoConvivienteCivilConHijos) {
            return;
        }

        if (estadoHijosPareja.existeHijoSoloCausante && !estadoHijosPareja.existeHijoComunConPareja) {
            erroresAgrupados.add(TipoErrorValidacionBeneficiarios.HIJO_SOLO_CAUSANTE_CON_PARENTESCO_INCORRECTO);
        }
    }

    private hijoTieneApellidoCausanteEnCualquierPosicion(
        beneficiario: Beneficiary,
        apellidoPaternoNorm: string
    ): boolean {
        const hijoApPNorm = this.normalizarApellido(beneficiario.apellidoPaterno || '');
        const hijoApMNorm = this.normalizarApellido(beneficiario.apellidoMaterno || '');
        return apellidoPaternoNorm === hijoApPNorm || apellidoPaternoNorm === hijoApMNorm;
    }

    private normalizarApellido(apellido: string): string {
        return apellido.trim().toLowerCase();
    }

    /**
     * Valida reglas que requieren información del conjunto completo de beneficiarios
     * @param requiereBeneficiarios Si requiere beneficiario según estado civil
     * @param tieneBeneficiarioRequerido Si encontró el beneficiario requerido
     * @param tieneBeneficiarioConHijos Si tiene beneficiario con código "con hijos"
     * @param tieneHijoEnLista Si tiene hijo en la lista
     * @param erroresAgrupados Set de errores agrupados acumulados
     */
    private validarConjuntoBeneficiarios(
        requiereBeneficiarios: boolean,
        tieneBeneficiarioRequerido: boolean,
        tieneBeneficiarioConHijos: boolean,
        tieneHijoEnLista: boolean,
        erroresAgrupados: Set<TipoErrorValidacionBeneficiarios>
    ): void {
        // Validar falta de beneficiario requerido
        if (requiereBeneficiarios && !tieneBeneficiarioRequerido) {
            erroresAgrupados.add(TipoErrorValidacionBeneficiarios.ESTADO_CIVIL_SIN_BENEFICIARIO);
        }

        // Validar inconsistencia: tiene beneficiario "con hijos" pero sin hijo en lista
        if (tieneBeneficiarioConHijos && !tieneHijoEnLista) {
            erroresAgrupados.add(TipoErrorValidacionBeneficiarios.BENEFICIARIO_CON_HIJOS_SIN_HIJO_EN_LISTA);
        }
    }

    /**
     * Verifica si el estado civil requiere beneficiarios
     * @param estadoCivil Estado civil del afiliado
     * @returns true si es casado o conviviente civil
     */
    private requiereBeneficiariosPorEstadoCivil(estadoCivil: string): boolean {
        return (
            estadoCivil === ESTADO_CIVIL_CASADO ||
            estadoCivil === ESTADO_CIVIL_CONVIVIENTE_CIVIL
        );
    }

    /**
     * Verifica si un código de parentesco corresponde al beneficiario requerido según estado civil
     * @param codigoParentesco Código del parentesco a verificar
     * @param estadoCivil Estado civil del afiliado
     * @returns true si el código corresponde al beneficiario requerido
     */
    private esParentescoRequeridoPorEstadoCivil(
        codigoParentesco: number,
        estadoCivil: string
    ): boolean {
        if (estadoCivil === ESTADO_CIVIL_CASADO) {
            return CODIGOS_CONYUGE.includes(codigoParentesco);
        }
        if (estadoCivil === ESTADO_CIVIL_CONVIVIENTE_CIVIL) {
            return CODIGOS_CONVIVIENTE_CIVIL.includes(codigoParentesco);
        }
        return false;
    }

    /**
     * Valida que un hijo beneficiario cumpla los requisitos
     * Reglas: Debe ser menor de 24 años Y estado civil soltero (S)
     * @param hijo Beneficiario con código de parentesco 30
     * @returns Objeto con error si no cumple, null si es válido
     */
    private validarHijo(
        hijo: Beneficiary
    ): { esValido: boolean; tipoError: TipoErrorValidacionBeneficiarios } | null {
        const edad = this.calcularEdad(hijo.fechaNacimiento);
        const estadoCivilCodigo = hijo.estadoCivil?.toUpperCase();

        // Validar: Debe ser menor de 24 años Y soltero (S)
        const cumpleEdad = edad < EDAD_MAXIMA_HIJO_BENEFICIARIO;
        const esSoltero = estadoCivilCodigo === CODIGO_ESTADO_CIVIL_SOLTERO;

        if (!cumpleEdad || !esSoltero) {
            return {
                esValido: false,
                tipoError: TipoErrorValidacionBeneficiarios.HIJO_NO_MENOR_24_SOLTERO
            };
        }

        return null; // Hijo válido
    }

    /**
     * Valida si los apellidos del hijo coinciden con los del causante/pareja sin importar el orden
     * Los hijos pueden tener los apellidos invertidos (decisión de los padres al nacer o posterior solicitud)
     * @param beneficiario Objeto beneficiario con apellidos del hijo
     * @param apellidoPaterno Primer apellido del causante/pareja
     * @param apellidoMaterno Segundo apellido del causante/pareja
     * @returns true si el hijo tiene exactamente esos dos apellidos en cualquier orden
     */
    private validarApellidosHijosEnComun(
        beneficiario: Beneficiary,
        apellidoPaterno: string,
        apellidoMaterno: string
    ): boolean {
        const hijoAp1 = beneficiario.apellidoPaterno?.trim().toLowerCase() ?? '';
        const hijoAp2 = beneficiario.apellidoMaterno?.trim().toLowerCase() ?? '';

        const ap1 = apellidoPaterno.trim().toLowerCase();
        const ap2 = apellidoMaterno.trim().toLowerCase();
        // Validar ambas combinaciones posibles (con y sin invertir)
        const coincidenciaNormal = hijoAp1 === ap1 && hijoAp2 === ap2;
        const coincidenciaInvertida = hijoAp1 === ap2 && hijoAp2 === ap1;

        return coincidenciaNormal || coincidenciaInvertida;
    }

    /**
     * Calcula la edad en años a partir de una fecha de nacimiento
     * @param fechaNacimiento Fecha en formato string "DD/MM/YYYY" (ej: "02/08/1989")
     * @returns Edad en años completos
     */
    private calcularEdad(fechaNacimiento: string): number {
        if (!fechaNacimiento) {
            return 0;
        }

        try {
            // Formato esperado del servicio: DD/MM/YYYY
            const [dia, mes, anio] = fechaNacimiento.split('/').map(Number);
            const fechaNac = new Date(anio, mes - 1, dia);

            const hoy = new Date();
            let edad = hoy.getFullYear() - fechaNac.getFullYear();
            const mesActual = hoy.getMonth();
            const mesNacimiento = fechaNac.getMonth();

            // Ajustar si no ha cumplido años este año
            if (mesActual < mesNacimiento ||
                (mesActual === mesNacimiento && hoy.getDate() < fechaNac.getDate())) {
                edad--;
            }

            return edad;
        } catch (error) {
            console.error('Error al calcular edad:', error);
            return 0;
        }
    }
}
